import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Organization } from '../../types';

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  tokenType?: string;
}

export type BusinessMode = 'carrier' | 'broker' | 'customer_portal';
export type UserRole = 'admin' | 'dispatcher' | 'driver' | 'accountant' | 'safety_manager' | 'broker_agent' | 'customer_admin' | 'customer_viewer';

export interface MockUserProfile {
  id: string;
  user: User;
  organization: Organization;
  description: string;
}

// Mock organizations for development
const carrierOrg: Organization = {
  id: 'org_carrier_001',
  name: 'Acme Freight LLC',
  dotNumber: '1234567',
  mcNumber: 'MC-987654',
  businessMode: 'carrier',
  status: 'active',
  settings: {
    timezone: 'America/Chicago',
    dateFormat: 'MM/DD/YYYY',
    distanceUnit: 'miles',
    currency: 'USD',
  },
  subscription: {
    plan: 'professional',
    status: 'active',
    currentPeriodEnd: '2025-12-31T23:59:59Z',
  },
  createdAt: '2023-06-01T00:00:00Z',
  updatedAt: '2024-11-15T00:00:00Z',
  roles: ['admin', 'dispatcher', 'driver', 'accountant', 'safety_manager']
};

const brokerOrg: Organization = {
  id: 'org_broker_001',
  name: 'Summit Logistics Group',
  dotNumber: '2345678',
  mcNumber: 'MC-123456',
  businessMode: 'broker',
  status: 'active',
  settings: {
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    distanceUnit: 'miles',
    currency: 'USD',
  },
  subscription: {
    plan: 'enterprise',
    status: 'active',
    currentPeriodEnd: '2025-12-31T23:59:59Z',
  },
  createdAt: '2022-03-15T00:00:00Z',
  updatedAt: '2024-12-01T00:00:00Z',
  roles: ['admin', 'broker_agent']
};

const customerOrg: Organization = {
  id: 'org_customer_001',
  name: 'ABC Corporation',
  businessMode: 'customer_portal',
  status: 'active',
  dotNumber: '',
  mcNumber: '',
  settings: {
    timezone: 'America/Chicago',
    dateFormat: 'MM/DD/YYYY',
    distanceUnit: 'miles',
    currency: 'USD',
  },
  subscription: {
    plan: 'customer_portal',
    status: 'active',
    currentPeriodEnd: '2025-12-31T23:59:59Z',
  },
  createdAt: '2024-06-15T00:00:00Z',
  updatedAt: '2024-12-01T00:00:00Z',
  roles: ['customer_admin', 'customer_viewer']
};

// Mock user profiles for different roles
export const mockUserProfiles: MockUserProfile[] = [
  {
    id: 'admin_carrier',
    description: 'Full access to all carrier operations',
    user: {
      id: 'usr_admin_001',
      email: 'admin@acmefreight.com',
      fullName: 'Michael Chen',
      role: 'admin',
      organizationId: 'org_carrier_001',
      permissions: ['*'],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2023-06-01T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...carrierOrg, roles: ['admin'] },
  },
  {
    id: 'dispatcher_carrier',
    description: 'Manages loads and driver assignments',
    user: {
      id: 'usr_dispatcher_001',
      email: 'dispatcher@acmefreight.com',
      fullName: 'Sarah Johnson',
      role: 'dispatcher',
      organizationId: 'org_carrier_001',
      permissions: [
        'loads:read',
        'loads:write',
        'loads:dispatch',
        'drivers:read',
        'drivers:write',
        'vehicles:read',
        'customers:read',
        'reports:read',
      ],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2024-01-15T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...carrierOrg, roles: ['dispatcher'] },
  },
  {
    id: 'driver_carrier',
    description: 'Views assigned loads and updates status',
    user: {
      id: 'usr_driver_001',
      email: 'driver@acmefreight.com',
      fullName: 'Marcus Williams',
      role: 'driver',
      organizationId: 'org_carrier_001',
      permissions: [
        'loads:read',
        'loads:update_status',
        'documents:upload',
        'settlements:read',
      ],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2024-08-20T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...carrierOrg, roles: ['driver'] },
  },
  {
    id: 'accountant_carrier',
    description: 'Manages invoices, settlements, and finances',
    user: {
      id: 'usr_accountant_001',
      email: 'accounting@acmefreight.com',
      fullName: 'Lisa Martinez',
      role: 'accountant',
      organizationId: 'org_carrier_001',
      permissions: [
        'loads:read',
        'invoices:read',
        'invoices:write',
        'settlements:read',
        'settlements:write',
        'reports:read',
        'reports:write',
        'customers:read',
      ],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2023-08-15T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...carrierOrg, roles: ['accountant'] },
  },
  {
    id: 'safety_carrier',
    description: 'Manages compliance and safety records',
    user: {
      id: 'usr_safety_001',
      email: 'safety@acmefreight.com',
      fullName: 'Robert Thompson',
      role: 'safety_manager',
      organizationId: 'org_carrier_001',
      permissions: [
        'drivers:read',
        'drivers:write',
        'vehicles:read',
        'vehicles:write',
        'compliance:read',
        'compliance:write',
        'documents:read',
        'documents:write',
        'reports:read',
      ],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2023-07-01T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...carrierOrg, roles: ['safety_manager'] },
  },
  {
    id: 'admin_broker',
    description: 'Full access to all broker operations',
    user: {
      id: 'usr_broker_admin_001',
      email: 'admin@summitlogistics.com',
      fullName: 'Jennifer Davis',
      role: 'admin',
      organizationId: 'org_broker_001',
      permissions: ['*'],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2022-03-15T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...brokerOrg, roles: ['admin'] },
  },
  {
    id: 'agent_broker',
    description: 'Books loads and manages carrier relationships',
    user: {
      id: 'usr_broker_agent_001',
      email: 'agent@summitlogistics.com',
      fullName: 'David Kim',
      role: 'broker_agent',
      organizationId: 'org_broker_001',
      permissions: [
        'loads:read',
        'loads:write',
        'carriers:read',
        'carriers:write',
        'customers:read',
        'customers:write',
        'marketplace:access',
        'reports:read',
      ],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2023-05-01T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...brokerOrg, roles: ['broker_agent'] },
  },
  {
    id: 'customer_admin',
    description: 'Full access to track shipments and view invoices',
    user: {
      id: 'usr_customer_admin_001',
      email: 'logistics@abccorp.com',
      fullName: 'Sarah Miller',
      role: 'customer_admin',
      organizationId: 'org_customer_001',
      permissions: [
        'portal:shipments:read',
        'portal:invoices:read',
        'portal:documents:read',
        'portal:quotes:request',
        'portal:reports:read',
        'portal:users:manage',
      ],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2024-06-15T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...customerOrg, roles: ['customer_admin'] },
  },
  {
    id: 'customer_viewer',
    description: 'View-only access to track shipments',
    user: {
      id: 'usr_customer_viewer_001',
      email: 'shipping@abccorp.com',
      fullName: 'John Peters',
      role: 'customer_viewer',
      organizationId: 'org_customer_001',
      permissions: [
        'portal:shipments:read',
        'portal:documents:read',
      ],
      isActive: true,
      onboardingComplete: true,
      createdAt: '2024-08-20T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
    },
    organization: { ...customerOrg, roles: ['customer_viewer'] },
  },
];

interface AuthStore {
  // State
  token: string | null;
  refreshToken: string | null;
  accessToken: string | null;
  expiresAt: string | null;
  authResponse: AuthResponse | null;
  loggedInAt: string | null;
  isAuthenticated: boolean;
  businessMode: BusinessMode;
  currentProfileId: string | null;
  needsOnboarding: boolean;
  isHydrated: boolean;
  // OTP flow state
  otpEmail: string | null;
  otpSession: string | null;
  otpFlow: 'login' | 'register' | null;
  // Actions
  setAuthResponse: (response: AuthResponse) => void;
  setTokens: (accessToken: string, refreshToken: string, expiresAt: string) => void;
  clearAuth: () => void;
  isTokenExpired: () => boolean;
  switchProfile: (profileId: string) => void;
  toggleBusinessMode: () => void;
  login: (user: User | null, organization: Organization | null) => void;
  logout: () => void;
  setNeedsOnboarding: (needs: boolean) => void;
  setHydrated: (hydrated: boolean) => void;
  setOtp: (email: string, session: string, flow: 'login' | 'register') => void;
  clearOtp: () => void;
}

const initialState = {
  organization: null,
  token: null,
  refreshToken: null,
  accessToken: null,
  expiresAt: null,
  authResponse: null,
  loggedInAt: null,
  isAuthenticated: false,
  businessMode: 'carrier' as BusinessMode,
  currentProfileId: null,
  needsOnboarding: false,
  isHydrated: false,
  otpEmail: null as string | null,
  otpSession: null as string | null,
  otpFlow: null as 'login' | 'register' | null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setAuthResponse: (response: AuthResponse) => {
        const now = new Date().toISOString();
        set({
          token: response.accessToken,
          refreshToken: response.refreshToken,
          accessToken: response.accessToken,
          expiresAt: response.expiresAt,
          authResponse: response,
          loggedInAt: now,
          isAuthenticated: true,
          businessMode: 'carrier' as BusinessMode,
        });
      },

      setTokens: (accessToken: string, refreshToken: string, expiresAt: string) => {
        set({
          token: accessToken,
          refreshToken,
          accessToken,
          expiresAt,
          authResponse: {
            accessToken,
            refreshToken,
            expiresAt,
          },
          isAuthenticated: true,
          businessMode: 'carrier' as BusinessMode,
        });
      },

      clearAuth: () => set(initialState),

      isTokenExpired: () => {
        const { expiresAt } = get();
        if (!expiresAt) return true;

        const expirationTime = new Date(expiresAt).getTime();
        const currentTime = new Date().getTime();

        return currentTime >= expirationTime;
      },

      switchProfile: (profileId: string) => {
        const profile = mockUserProfiles.find(p => p.id === profileId);
        if (profile) {
          set({
            currentProfileId: profileId,
          });
        }
      },

      toggleBusinessMode: () => {
        const { businessMode } = get();
        const newMode: BusinessMode = businessMode === 'carrier' ? 'broker' : businessMode === 'broker' ? 'customer_portal' : 'carrier';
        set({ businessMode: newMode });
      },

      login: (user: User | null, organization: Organization | null) => set({
        isAuthenticated: !!user,
        needsOnboarding: !organization,
      }),

      logout: () => {
        set(initialState);
      },

      setNeedsOnboarding: (needs: boolean) => set({ needsOnboarding: needs }),

      setHydrated: (hydrated: boolean) => set({ isHydrated: hydrated }),

      setOtp: (email: string, session: string, flow: 'login' | 'register') => set({
        otpEmail: email,
        otpSession: session,
        otpFlow: flow,
      }),

      clearOtp: () => set({
        otpEmail: null,
        otpSession: null,
        otpFlow: null,
      }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => {
        return {
          token: state.token,
          refreshToken: state.refreshToken,
          accessToken: state.accessToken,
          expiresAt: state.expiresAt,
          authResponse: state.authResponse,
          loggedInAt: state.loggedInAt,
          isAuthenticated: state.isAuthenticated,
          businessMode: state.businessMode,
          currentProfileId: state.currentProfileId,
          needsOnboarding: state.needsOnboarding,
          isHydrated: state.isHydrated,
        }
      },
    }
  )
);
